import { BiologicalSex, MOSOption, GeneratedImages, AllMOSImages } from '../types';
import { MOS_OPTIONS } from '../constants';

// Use relative URL for Vercel deployment, fallback to localhost for local dev
const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3002';

// Helper to replace {sex} placeholder with actual gender term
// For males, also adds clean-shaven requirement (military grooming standards)
function insertSexIntoPrompt(prompt: string, sex: BiologicalSex): string {
  const genderTerm = sex === 'male' ? 'male' : 'female';
  let processedPrompt = prompt.replace(/{sex}/g, genderTerm);

  // For male subjects, add clean-shaven requirement (military grooming standards)
  if (sex === 'male') {
    processedPrompt = processedPrompt.replace(
      /Maintain facial identity/gi,
      'Clean shaven with no facial hair. Maintain facial identity'
    );
  }

  return processedPrompt;
}

// Helper to replace {lastName} placeholder with user's last name
function insertLastNameIntoPrompt(prompt: string, lastName: string): string {
  return prompt.replace(/{lastName}/g, lastName);
}

// Generate a single image using Gemini API (synchronous - no polling needed!)
async function generateSingleImage(
  imageBase64: string,
  prompt: string
): Promise<string> {
  const maxRetries = 3;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Calling Gemini API (attempt ${attempt}/${maxRetries})...`);

      const response = await fetch(`${BACKEND_URL}/api/gemini-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: imageBase64,
          prompt: prompt,
          aspectRatio: '3:4',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Gemini API error:', response.status, data);
        throw new Error(data.error || `Backend request failed: ${response.status}`);
      }

      if (data.success && data.imageBase64) {
        console.log('Gemini generation successful');
        return data.imageBase64;
      }

      throw new Error(data.error || 'Failed to generate image');
    } catch (error) {
      lastError = error as Error;
      console.error(`Generation attempt ${attempt} failed:`, error);

      // If it's a rate limit error (429), wait and retry
      if (lastError.message.includes('429') && attempt < maxRetries) {
        console.log(`Rate limited, waiting 2s before retry...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else if (attempt < maxRetries) {
        // For other errors, small delay before retry
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  throw lastError || new Error('Generation failed after retries');
}

// Generate dual portraits - portrait and field action shot
// Makes TWO separate API calls in parallel
export async function generateDualPortraits(
  imageBase64: string,
  sex: BiologicalSex,
  mos: MOSOption,
  lastName?: string
): Promise<GeneratedImages> {
  console.log('Starting dual image generation with Gemini API...');
  console.log('Sex:', sex, 'MOS:', mos.title, 'LastName:', lastName);

  // Prepare prompts with sex and lastName variables replaced
  let portraitPrompt = insertSexIntoPrompt(mos.portraitPrompt, sex);
  let fieldPrompt = insertSexIntoPrompt(mos.fieldPrompt, sex);

  if (lastName) {
    portraitPrompt = insertLastNameIntoPrompt(portraitPrompt, lastName);
    fieldPrompt = insertLastNameIntoPrompt(fieldPrompt, lastName);
  }

  console.log('Portrait prompt:', portraitPrompt.substring(0, 100) + '...');
  console.log('Field prompt:', fieldPrompt.substring(0, 100) + '...');

  // Make TWO separate API calls in parallel using Promise.all
  // Gemini API is synchronous - no polling needed!
  const [portraitImage, fieldImage] = await Promise.all([
    generateSingleImage(imageBase64, portraitPrompt),
    generateSingleImage(imageBase64, fieldPrompt),
  ]);

  console.log('Both images generated successfully');
  console.log('Portrait length:', portraitImage.length);
  console.log('Field length:', fieldImage.length);

  return {
    portrait: portraitImage,
    field: fieldImage,
  };
}

// Generate ALL MOS images (12 total: 6 MOS × 2 images each)
// ALL MOS start generating in parallel, but priority callback fires first
// This maximizes parallelism while still showing priority MOS results immediately
export async function generateAllMOSImages(
  imageBase64: string,
  sex: BiologicalSex,
  priorityMosId: string,
  lastName?: string,
  onPriorityComplete?: (images: GeneratedImages) => void,
  onMOSComplete?: (mosId: string, images: GeneratedImages) => void
): Promise<AllMOSImages> {
  console.log('Starting batch generation for all MOS with Gemini API...');
  console.log('Priority MOS:', priorityMosId, 'Sex:', sex, 'LastName:', lastName);

  const allImages: AllMOSImages = {};
  const priorityMOS = MOS_OPTIONS.find(m => m.id === priorityMosId);

  if (!priorityMOS) {
    throw new Error(`Priority MOS not found: ${priorityMosId}`);
  }

  // Track if priority callback has been fired
  let priorityCallbackFired = false;

  // Start ALL MOS generation in parallel for maximum speed
  console.log('🔄 Starting parallel generation for ALL 6 MOS...');

  const allPromises = MOS_OPTIONS.map(async (mos) => {
    const isPriority = mos.id === priorityMosId;

    try {
      console.log(`🔄 Generating MOS: ${mos.id}${isPriority ? ' (PRIORITY)' : ''}...`);
      const images = await generateDualPortraits(imageBase64, sex, mos, lastName);
      allImages[mos.id] = images;

      console.log(`🔄 MOS ${mos.id} generation complete`);

      // Fire priority callback IMMEDIATELY when priority completes
      if (isPriority && !priorityCallbackFired) {
        priorityCallbackFired = true;
        console.log('Priority MOS complete, firing callback...');
        if (onPriorityComplete) {
          onPriorityComplete(images);
        }
      }

      // Fire onMOSComplete for all MOS (including priority)
      if (onMOSComplete) {
        onMOSComplete(mos.id, images);
        console.log(`🔄 MOS ${mos.id} callback fired successfully`);
      }

      return { mosId: mos.id, success: true };
    } catch (error) {
      console.error(`🔄 MOS ${mos.id} generation failed:`, error);

      // If priority fails, we need to throw
      if (isPriority) {
        throw error;
      }

      // For non-priority, continue with other MOS
      return { mosId: mos.id, success: false, error };
    }
  });

  // Wait for all to complete
  await Promise.all(allPromises);

  console.log('All MOS generation complete. Successful:', Object.keys(allImages).length);
  return allImages;
}

// Legacy function for backwards compatibility (not used anymore)
export async function generateMilitaryPortrait(
  imageBase64: string,
  sex: BiologicalSex,
  mos: MOSOption
): Promise<string> {
  const prompt = insertSexIntoPrompt(mos.portraitPrompt, sex);
  return generateSingleImage(imageBase64, prompt);
}
